package one.healthium.log_water.ui.internal

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import ifaandroid.feature.profile.log_water.generated.resources.Res
import ifaandroid.feature.profile.log_water.generated.resources.ic_bottle
import ifaandroid.feature.profile.log_water.generated.resources.ic_minus
import ifaandroid.feature.profile.log_water.generated.resources.ic_plus
import ifaandroid.feature.profile.log_water.generated.resources.img_water_footer
import loco.LOG_WATER_CLOSE_BUTTON
import loco.LOG_WATER_PERSONAL_AMOUNT_LABEL
import loco.LOG_WATER_SCREEN_TITLE
import one.healthium.base.utils.toVolumeString
import one.healthium.base.utils.toVolumeUnit
import one.healthium.loco.resolve
import one.healthium.ui.compose.components.AppButton
import one.healthium.ui.compose.components.AppButtonDefaults
import one.healthium.ui.compose.components.AppIconButton
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black50
import one.healthium.ui.compose.theme.gray_2
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
internal fun LogWaterControl(
    modifier: Modifier = Modifier,
    value: LogWaterUiModel,
    isMetric: Boolean,
    onIncreaseClicked: () -> Unit,
    onDecreaseClicked: () -> Unit,
    onCloseClicked: () -> Unit,
) {
    Box(modifier) {
        Image(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter),
            painter = painterResource(AppTheme.logWaterControlStyle.footerImage ?: Res.drawable.img_water_footer),
            contentScale = ContentScale.Crop,
            alignment = Alignment.TopCenter,
            contentDescription = null,
        )
        WaterIntakeInput(
            value = value,
            isMetric = isMetric,
            onIncreaseClicked = onIncreaseClicked,
            onDecreaseClicked = onDecreaseClicked,
            onCloseClicked = onCloseClicked,
        )
    }
}

@Composable
private fun WaterIntakeInput(
    value: LogWaterUiModel,
    isMetric: Boolean,
    onIncreaseClicked: () -> Unit,
    onDecreaseClicked: () -> Unit,
    onCloseClicked: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Spacer(Modifier.height(20.dp))
        Icon(
            modifier = Modifier.size(46.dp),
            painter = painterResource(Res.drawable.ic_bottle),
            tint = black50,
            contentDescription = null,
        )
        Spacer(Modifier.height(16.dp))
        Text(
            modifier = Modifier.padding(horizontal = 30.dp),
            text = stringResource(loco.Res.string.LOG_WATER_SCREEN_TITLE),
            style = AppTheme.logWaterControlStyle.resolvedTitleStyle,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(16.dp))
        Text(
            modifier = Modifier.padding(horizontal = 30.dp),
            text = stringResource(loco.Res.string.LOG_WATER_PERSONAL_AMOUNT_LABEL),
            style = AppTheme.textStyles.secondary_14_400,
            color = gray_3,
            textAlign = TextAlign.Center,
        )
        Text(
            modifier = Modifier.padding(horizontal = 30.dp).testTag("daily_amount"),
            text = "${value.dailyAmount.toVolumeString(isMetric)} ${isMetric.toVolumeUnit().resolve()}",
            style = AppTheme.textStyles.poppins14Bold,
            color = AppTheme.colors.primary,
        )
        Spacer(Modifier.height(30.dp))
        Progress(value, isMetric)
        Spacer(Modifier.height(30.dp))
        ButtonBar(onDecreaseClicked, onIncreaseClicked)
        Spacer(Modifier.height(30.dp))
        AppButton(
            modifier = Modifier.defaultMinSize(minWidth = 176.dp).testTag("log_water_close"),
            onClick = onCloseClicked,
            colors = AppTheme.logWaterControlStyle.saveButtonStyleRef.let {
                AppButtonDefaults.resolveColors(it)
            }
        ) {
            Text(stringResource(loco.Res.string.LOG_WATER_CLOSE_BUTTON))
        }
        Spacer(Modifier.height(65.dp))
    }
}

@Composable
private fun ButtonBar(onDecreaseClicked: () -> Unit, onIncreaseClicked: () -> Unit) {
    Row {
        AppIconButton(
            modifier = Modifier.size(60.dp).testTag("water_decrease"),
            onClick = onDecreaseClicked,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_minus),
                contentDescription = null,
            )
        }
        Spacer(Modifier.width(24.dp))
        AppIconButton(
            modifier = Modifier.size(60.dp).testTag("water_increase"),
            onClick = onIncreaseClicked,
        ) {
            Icon(
                painter = painterResource(Res.drawable.ic_plus),
                contentDescription = null,
            )
        }
    }
}

@Composable
private fun Progress(value: LogWaterUiModel, isMetric: Boolean) {
    val animatedProgress by animateFloatAsState(
        targetValue = value.progress,
        animationSpec = tween(durationMillis = 200),
    )
    Box(
        contentAlignment = Alignment.Center
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(197.dp),
            color = AppTheme.logWaterControlStyle.resolvedProgressColor,
            trackColor = gray_2,
            strokeWidth = 20.dp,
            strokeCap = StrokeCap.Round,
            progress = { animatedProgress },
        )
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                modifier = Modifier.testTag("water_intake_progress"),
                text = value.value.toVolumeString(isMetric),
                style = AppTheme.textStyles.poppins30Bold,
            )
            Spacer(Modifier.height(6.dp))
            Text(
                text = isMetric.toVolumeUnit().resolve(),
                style = AppTheme.textStyles.secondary_18_400,
            )
        }
    }
}
